import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CAPTCHA_FAILED, completeAuthSession, loginUser } from '../../store/slices/authSlice';
import { RecaptchaError, RecaptchaNotice, RecaptchaProvider, useRecaptchaState, useRecaptchaToken } from '../../hooks/useRecaptcha';
import Input from '../../components/ui/Input';
import robotIllustration from '../../assets/img/Illustration-Login.png';
import { Footer } from '../../components/layout/Footer';
import { Header } from '../../components/layout/Header';
import { useTranslation } from 'react-i18next';
import LoginLayout from '../../components/layout/LoginLayout';
import { addNotification } from '../../store/slices/notificationSlice';
import { helper } from '../../utils/helper';
import type { AppDispatch, RootState } from '../../store/store';

const LoginPageInner: React.FC = () => {
  const { isAuthenticated, loading, error } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const getRecaptchaToken = useRecaptchaToken();
  const recaptchaState = useRecaptchaState();
  // Inline captcha_failed_help notice: set on 422 or a failed token fetch.
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    twoFactorCode?: string;
  }>({});

  const { t } = useTranslation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validatePassword = useCallback((value: string) => {
    if (!value) {
      return t('validation_password_required');
    }
    if (value.length < 6) {
      return t('validation_password_short');
    }
    return '';
  }, [t]);

  const handleEmailBlur = () => {
    setErrors(prev => ({
      ...prev,
      email: helper.validateEmail(email) ? t('validation_email_invalid') : ''
    }));
  };

  const handlePasswordBlur = () => {
    setErrors(prev => ({
      ...prev,
      password: validatePassword(password)
    }));
  };

  const validateForm = () => {
    const newErrors = {
      email: helper.validateEmail(email) ? t('validation_email_invalid') : '',
      password: validatePassword(password)
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (showTwoFactor) {
      if (!twoFactorCode || twoFactorCode.length !== 6) {
        setErrors(prev => ({ ...prev, twoFactorCode: t('two_factor.error_code_invalid') }));
        return;
      }
    } else {
      if (!validateForm()) {
        return;
      }
    }

    setCaptchaFailed(false);

    // A fresh token per submit: v3 tokens are single-use, so the 2FA second
    // step must not reuse the token from the first submit.
    let captchaToken: string | null = null;
    try {
      captchaToken = await getRecaptchaToken('login');
    } catch (err) {
      if (err instanceof RecaptchaError) {
        // Do not send the request without a token - it is guaranteed to 422.
        setCaptchaFailed(true);
        return;
      }
      throw err;
    }

    try {
      const result = await dispatch(loginUser({
        email,
        password,
        twoFactorCode: showTwoFactor ? twoFactorCode : undefined,
        captchaToken,
      }));
      
      if (loginUser.fulfilled.match(result)) {
        if (result.payload.type === 'two_factor_required') {
          setShowTwoFactor(true);
          setTwoFactorCode('');
          return;
        }
        // Run the centralized post-auth handshake (token persist, OpenAPI,
        // bootstrap of profile/ds/subscriptions/workspace-config) before
        // routing into the dashboard so the sidebar, guards and CM pages
        // already see the fresh data on first paint.
        await dispatch(completeAuthSession(result.payload.data));
        navigate('/dashboard');
      } else if (loginUser.rejected.match(result)) {
        if (showTwoFactor) {
          setTwoFactorCode('');
        }
        if (result.payload === CAPTCHA_FAILED) {
          // Inline notice is the requirement; the toast is kept on top of it.
          setCaptchaFailed(true);
        }
        dispatch(addNotification({
          type: 'error',
          title: t('notification_error'),
          message: result.payload === CAPTCHA_FAILED
            ? t('captcha_failed')
            : (result.payload || t('login_failed')),
        }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: t('login_failed'),
      }));
    }
  };

  return (
    <LoginLayout>
      <div className="login-illustration">
        <img src={robotIllustration} alt={t('welcome')} />
      </div>

      <Header isLoginHeader={true} showLoginHeader={true} />
      
      <div className="login-content">
        <div className="login-form-container">
          <div className="login-titles">
            <h1>{t('welcome')}</h1>
            <p>{t('loginIntoYourAccount')}</p>
          </div>
          
          <form onSubmit={handleLogin} className="login-form">
            {!showTwoFactor ? (
              <>
                <Input
                  id="email"
                  label={t('emailAddress')}
                  type="email"
                  value={email}
                  error={errors.email}
                  onChange={setEmail}
                  onBlur={handleEmailBlur}
                  required
                />
                
                <Input
                  id="password"
                  label={t('password')}
                  type="password"
                  value={password}
                  error={errors.password}
                  onChange={setPassword}
                  onBlur={handlePasswordBlur}
                  required
                />
                
                <Link to="/forgot-password" className="forgot-link">
                  {t('forgotPassword')}
                </Link>
              </>
            ) : (
              <div className="two-factor-login">
                <p className="two-factor-login-description">{t('two_factor.login_description')}</p>
                <Input
                  id="twoFactorCode"
                  label={t('two_factor.code_label')}
                  value={twoFactorCode}
                  error={errors.twoFactorCode}
                  onChange={(value) => {
                    const cleaned = value.replace(/\D/g, '').slice(0, 6);
                    setTwoFactorCode(cleaned);
                    if (errors.twoFactorCode) {
                      setErrors(prev => ({ ...prev, twoFactorCode: undefined }));
                    }
                  }}
                  placeholder="000000"
                />
                <button
                  type="button"
                  className="two-factor-back-link"
                  onClick={() => {
                    setShowTwoFactor(false);
                    setTwoFactorCode('');
                  }}
                >
                  {t('two_factor.back_to_login')}
                </button>
              </div>
            )}
            
            {recaptchaState === 'blocked' && <RecaptchaNotice variant="blocked" />}
            {recaptchaState !== 'blocked' && captchaFailed && <RecaptchaNotice variant="failed" />}

            <button 
              type="submit" 
              className="login-button"
              disabled={loading || recaptchaState !== 'ready'}
            >
              {loading || recaptchaState === 'loading' ? t('notification_loading') : (showTwoFactor ? t('two_factor.verify_button') : t('login'))}
            </button>
            
            {!showTwoFactor && (
              <Link to="/register" className="registration-link">
                {t('registerAccount')}
              </Link>
            )}
          </form>
        </div>
      </div>
      <Footer />
    </LoginLayout>
  );
};

const LoginPage: React.FC = () => (
  <RecaptchaProvider>
    <LoginPageInner />
  </RecaptchaProvider>
);

export default LoginPage;
